<script setup>
import { computed, ref, getCurrentInstance } from 'vue'
import { useNotification } from "@kyvg/vue3-notification"
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import DynamicService from '@/services/DynamicService'
import IconBase from '@/components/icon/IconBase.vue'
import IconBin from '@/components/icon/IconBin.vue'
import IconPlus from '@/components/icon/IconPlus.vue'
import IconElipsis from '@/components/icon/IconElipsis.vue'
import IconChevronUpDown from '@/components/icon/IconChevronUpDown.vue'
import BackButton from '@/components/Atoms/BackButton.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import Title from '@/components/ui/Title.vue'
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue'
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue'
import HeaderHelperLinks from '@/components/Moleculas/HeaderHelperLinks.vue'

const { notify } = useNotification()
const instance = getCurrentInstance()
const root = instance.proxy.$root
const store = useStore()
const route = useRoute()
const router = useRouter()

const emit = defineEmits(['delete', 'add-player'])

const mobileDropdown = ref(null)

const player = computed(() => store.getters['DynamicPlayers/dynamicPlayer'])
const players = computed(() => store.getters['DynamicPlayers/dynamicPlayers'])

const saveNewPlayerName = async (newName) => {
  if (newName !== player.value.name) {
    await DynamicService.renamePlayer(player.value._id, newName)
    notify({ group: 'info', text: 'Your player was renamed' })
    await store.dispatch('DynamicPlayers/getPlayer', route.params?.playerId)
    await store.dispatch('DynamicPlayers/getPlayers', true)
  }
}

const togglePlayer = async () => {
  await DynamicService.togglePlayer(player.value._id)
  mobileDropdown.value?.toggleDropdown()
  await store.dispatch('DynamicPlayers/getPlayer', route.params?.playerId)
  notify({ group: 'success', text: 'Status changed' })
}

const showDeleteModal = () => {
  mobileDropdown.value?.toggleDropdown()
  emit('delete')
}

const goToPlayer = async (id) => {
  if (id !== route.params.playerId) {
    await store.dispatch('DynamicPlayers/setCurrentPlayer', id)
    await store.dispatch('DynamicPlayers/getPlayer', id)
    router.push({ name: "DynamicManage", params: { playerId: id } })
  }
}
</script>

<template>
  <header class="flex items-center w-full gap-x-3 gap-y-2 flex-wrap lg:flex-nowrap">
    <BackButton to="/dynamic" title="Back to Dynamic Players" />
    <Title class="min-w-0 flex-1 lg:flex-none" :editable="true"
      titleClasses="-mr-2 px-[0.5rem] rounded-md hover:bg-surface-3 line-clamp-2 text-lg font-semibold min-h-8 min-w-0"
      edit-class="bg-surface-3 line-clamp-none" :title="player?.name" @changed="saveNewPlayerName" />
    <HeaderHelperLinks class="order-last w-full lg:order-none lg:w-auto lg:ml-auto lg:mr-1"
      howItWorksLink="https://docs.castr.com/en/articles/7785953-how-to-create-a-dynamic-player"
      howItWorksText="How Dynamic Player works" tutorialLink="9UJ1aZUoaRqiJ86YfFYKG" />
    <div class="flex items-center gap-x-2 shrink-0 ml-auto lg:ml-0">
      <FormButton @click="togglePlayer" type="secondary" size="md"
        :label="player?.enabled ? 'Disable' : 'Enable'" />
      <FormButton @click="showDeleteModal" isIcon type="danger" size="md" title="Delete player">
        <icon-base fill="none">
          <IconBin />
        </icon-base>
      </FormButton>
    </div>
  </header>
</template>
